/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useMemo } from 'react';
import { MonitorProcessData } from '@/types/models.ts';
import { CButtonRounded } from '@/components';
import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AppRoutes } from '@/constants/routes.ts';
import { Utils } from '@/utils/utils.ts';
import { classNames } from 'primereact/utils';
import { History } from 'lucide-react';
import { useJobsProcessInfoData } from './JobsMonitorContext';

interface Props {
  className?: string;
  monitorData: MonitorProcessData;
}
export const ActionRender: React.FC<Props> = ({ className, monitorData }) => {
  const { t } = useTranslation();

  const { processesInfoData } = useJobsProcessInfoData();

  const rowProcessInfo = useMemo(() => {
    if (processesInfoData && monitorData) {
      const rowInfo = processesInfoData?.find(
        (el) => el.fecthStatus === 'sucessfull' && el.processId == monitorData.id
      );
      return rowInfo?.data;
    }
  }, [processesInfoData, monitorData]);

  const url = useMemo(() => {
    const url = AppRoutes.jobs.processHistory.url(monitorData.id);
    if (!rowProcessInfo) {
      return url;
    }
    if (monitorData.id.startsWith('dyna~')) {
      return AppRoutes.jobs.processHistory.url(monitorData.id);
    }

    if (rowProcessInfo.link) {
      return url + Utils.buildWithQueryParamsUrl({ job_reference: rowProcessInfo.project_name });
    }
    return '#';
  }, [rowProcessInfo, monitorData]);

  return (
    <div className={classNames(className, 'w-full')}>
      <NavLink to={url}>
        <CButtonRounded
          rounded
          icon={<History size={16} />}
          tooltipOptions={{ className: 'text-sm', position: 'top' }}
          tooltip={t(['actions.show_job_history', 'History'])}
        />
      </NavLink>
    </div>
  );
};
